import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Navigation, Wallet, CheckCircle2, Clock } from "lucide-react";
import api, { money } from "@/lib/api";
import Logo from "@/components/Logo";

export default function Pass() {
  const { id } = useParams();
  const [p, setP] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); api.get(`/passes/${id}`).then((r) => setP(r.data)); }, [id]);
  if (!p) return <div className="min-h-screen grid place-items-center text-stone-400">Loading…</div>;

  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=0&data=${encodeURIComponent(p.code)}`;
  const used = p.status === "used";

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-white"><div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between"><Link to="/" className="text-2xl"><Logo /></Link><Link to="/account" className="text-sm font-bold text-stone-500">My Friibs</Link></div></header>

      <div className="max-w-md mx-auto px-4 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl bg-white friib-shadow-hover overflow-hidden border border-stone-100" data-testid="friib-pass">
          <div className="relative">
            <img src={p.offer_image} alt="" className="w-full h-40 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-4 left-5 text-white">
              <p className="text-xs font-bold uppercase tracking-wider opacity-90">{p.business_name}</p>
              <h1 className="font-display text-2xl font-extrabold">{p.offer_title}</h1>
            </div>
            {used && <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-white/90 text-xs font-bold text-stone-700 flex items-center gap-1"><CheckCircle2 size={13} /> Used</div>}
          </div>

          <div className="p-6 text-center">
            <p className="text-sm text-stone-500 mb-1">Show this code at {p.business_name}</p>
            <div className={`inline-block p-4 rounded-2xl bg-white border-2 ${used ? "border-stone-200 opacity-40" : "border-[var(--mint)]"}`}>
              <img src={qr} alt="QR code" className="w-44 h-44" data-testid="pass-qr" />
            </div>
            <p className="mt-3 font-mono font-bold text-lg tracking-wider text-stone-900" data-testid="pass-code">{p.code}</p>
            <p className="text-xs text-stone-400">Value up to {money(p.normal_value)} · {p.purchase_requirement}</p>
          </div>

          <div className="border-t-2 border-dashed border-stone-200 relative">
            <span className="absolute -left-3 -top-3 w-6 h-6 rounded-full bg-stone-50" /><span className="absolute -right-3 -top-3 w-6 h-6 rounded-full bg-stone-50" />
          </div>

          <div className="p-6 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-stone-600"><MapPin size={15} style={{ color: "var(--mint)" }} /> {p.business_address}</div>
            <div className="flex items-center gap-2 text-stone-600"><Clock size={15} style={{ color: "var(--mint)" }} /> Expires {new Date(p.expiration).toLocaleDateString()}</div>
            <p className="text-stone-500 pt-1">{p.redemption_instructions}</p>
            <div className="grid grid-cols-2 gap-3 pt-3">
              <a href={`https://maps.google.com/?q=${p.business_lat},${p.business_lng}`} target="_blank" rel="noreferrer" data-testid="directions-btn"
                className="text-center py-3 rounded-full font-bold text-white flex items-center justify-center gap-1.5" style={{ background: "var(--mint)" }}><Navigation size={15} /> Directions</a>
              <button className="py-3 rounded-full font-bold text-stone-900 bg-stone-100 flex items-center justify-center gap-1.5" data-testid="add-wallet-btn"><Wallet size={15} /> Add to Wallet</button>
            </div>
          </div>
        </motion.div>

        {p.upsell && !used && (
          <div className="mt-6 rounded-3xl bg-white border border-stone-100 friib-shadow p-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--friib)] mb-2">Make your visit even better</p>
            <div className="flex gap-4 items-center">
              <img src={p.upsell.image} alt="" className="w-16 h-16 object-cover rounded-xl" />
              <div className="flex-1"><p className="font-display font-bold text-stone-900">{p.upsell.name}</p><p className="text-sm"><span className="line-through text-stone-400 mr-1">{money(p.upsell.regular_price)}</span><span className="font-bold" style={{ color: "var(--friib)" }}>{money(p.upsell.upsell_price)}</span></p></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
